import React, { useState, useEffect, useCallback } from 'react';
import { useCreateEvent } from '../../../hooks/useCreateEvent';
import useUserContext from '../../../hooks/useUserContext';
import { DatabaseEvent, EventState, Question, Tag, WatchParty } from '../../../types/types';
import { useEventTags } from '../../../hooks/useEventTags';
import { updateEvent } from '../../../services/eventService';
import './index.css';
import { addQuestion } from '../../../services/questionService';
import { createWatchParty } from '../../../services/watchService';

/**
 * EventFormProps interface
 *
 * @date - The date for the event.
 * @onClose - Function to close the event form.
 * @onEventCreate - Function to handle event creation.
 * @event - Optional event object for editing an existing event.
 */
interface EventFormProps {
  date: Date;
  onClose: () => void;
  onEventCreate: (updatedEvent: Partial<DatabaseEvent>) => void;
  event?: Partial<DatabaseEvent>;
}

/**
 * EventForm component is used for creating or editing events.
 * It includes fields for title, description, start time, end time, visibility,
 * and allows the user to select a tag for the event.
 * Additionally, if specific tags like "Watch Party" or "Q+A" are selected,
 * it provides extra fields for creating related entities (watch party or question).
 *
 * @param date The date for the event.
 * @param onClose Function to close the form when cancelled.
 * @param onEventCreate Callback function to handle creation or updates of events.
 * @param event Optional event data if editing an existing event.
 */
const EventForm: React.FC<EventFormProps> = ({ date, onClose, onEventCreate, event }) => {
  const { eventTags } = useEventTags();
  const formatDate = useCallback(
    (d?: Date) => (d ? new Date(d).toISOString().slice(0, 16) : date.toISOString().slice(0, 16)),
    [date],
  );

  const [title, setTitle] = useState<string>(event?.eventTitle || '');
  const [description, setDescription] = useState<string>(event?.eventDescription || '');
  const [startTime, setStartTime] = useState(formatDate(event?.timeStart));
  const [endTime, setEndTime] = useState(formatDate(event?.timeEnd));
  const [selectedTag, setSelectedTag] = useState<string | null>(event?.tag || null);
  const [visibility, setVisibility] = useState<'public' | 'private'>('public');
  const [isMajor, setIsMajor] = useState<boolean>(event?.isMajor ?? false);

  const { createNewEvent, isSubmitting } = useCreateEvent();
  const { user: currentUser } = useUserContext();

  const [watchPartyTitle, setWatchPartyTitle] = useState('');

  const [questionTitle, setQuestionTitle] = useState('');
  const [questionText, setQuestionText] = useState('');
  const [questionTags, setQuestionTags] = useState('');
  const [error, setError] = useState<string>('');

  // Initialize form fields when editing an existing event
  useEffect(() => {
    if (event) {
      setTitle(event?.eventTitle || '');
      setDescription(event.eventDescription || '');
      setStartTime(formatDate(event.timeStart));
      setEndTime(formatDate(event.timeEnd));
      setSelectedTag(event.tag || null);
    }
  }, [event, formatDate]);

  /**
   * Handles the form submission for creating or updating an event.
   * Also creates related entities like watch parties or questions if appropriate tags are selected.
   *
   * @param e The form submission event.
   */
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Create event data object
    const updatedEvent: Partial<DatabaseEvent> = {
      ...(event && event._id ? { _id: event._id } : {}),
      state: event?.state ?? ({ status: 'WAITING_TO_START' } as EventState),
      eventTitle: title,
      timeStart: new Date(startTime),
      timeEnd: new Date(endTime),
      eventDescription: description,
      userCreated: event?.userCreated || currentUser.username,
      tag: selectedTag || undefined,
      userParticipating: event?.userParticipating ?? [],
      relatedAttachments: event?.relatedAttachments ?? [],
      visibility,
      isMajor,
    };

    try {
      if (event?._id) {
        await updateEvent(event._id.toString(), updatedEvent);
      } else {
        await createNewEvent(updatedEvent);
      }

      onEventCreate(updatedEvent);

      // Create watchparty if tag is Watch Party
      if (!event?._id && selectedTag === 'Watch Party' && watchPartyTitle.trim()) {
        try {
          const watchParty: WatchParty = {
            name: watchPartyTitle.trim(),
            host: currentUser?.username || 'defaultUser',
            isPublic: visibility === 'public',
            isOver: false,
            videoQueue: [],
            currentVideo: {
              title: '',
              url: '',
            },
            chat: {
              messages: [],
              participants: [],
            },
          };
          await createWatchParty(watchParty);
        } catch (watchError) {
          setError('Error creating watch party');
        }
      }

      // Create question if tag is Q+A
      if (!event?._id && selectedTag === 'Q+A' && questionTitle.trim()) {
        try {
          const tags: Tag[] = [
            {
              name: questionTags.trim(),
              description: 'User-defined tag',
            },
          ];

          const question: Question = {
            title: questionTitle.trim(),
            text: questionText.trim(),
            tags,
            askedBy: currentUser?.username || 'defaultUser',
            askDateTime: new Date(),
            answers: [],
            views: [],
            upVotes: [],
            downVotes: [],
            comments: [],
          };
          await addQuestion(question);
        } catch (questionError) {
          setError('Error creating question');
        }
      }

      onClose();
    } catch (err) {
      setError('Error creating event');
    }
  };

  /**
   * Determines the text for the submit button based on the form submission state.
   *
   * @returns The button text.
   */
  const getSubmitButtonText = () => {
    if (isSubmitting) return 'Submitting...';
    return event ? 'Save Changes' : 'Create Event';
  };

  return (
    <div>
      <h3>{event ? 'Edit Event' : 'Create Event'}</h3>
      <form onSubmit={handleSubmit}>
        <label>Title</label>
        <input type='text' value={title} onChange={e => setTitle(e.target.value)} required />

        <label>Description</label>
        <textarea value={description} onChange={e => setDescription(e.target.value)} />

        <div className='tag-selection'>
          <label>Select a Tag (optional):</label>
          <div className='tag-options'>
            {eventTags.map(tag => (
              <button
                key={tag.name}
                className={`tag-button ${selectedTag === tag.name ? 'selected' : ''}`}
                style={{
                  backgroundColor: selectedTag === tag.name ? tag.color : '#ddd',
                }}
                onClick={() => setSelectedTag(selectedTag === tag.name ? null : tag.name)}
                type='button'>
                {tag.name}
              </button>
            ))}
          </div>
        </div>

        {selectedTag === 'Watch Party' && (
          <div className='watchparty-extra-field'>
            <label>Watchparty Title</label>
            <input
              type='text'
              placeholder='Enter Watchparty Title'
              className='form-input'
              value={watchPartyTitle}
              onChange={e => setWatchPartyTitle(e.target.value)}
              required
            />
          </div>
        )}

        {selectedTag === 'Q+A' && (
          <div className='qna-extra-fields'>
            <label>Question Title</label>
            <input
              type='text'
              value={questionTitle}
              onChange={e => setQuestionTitle(e.target.value)}
              placeholder='Limit title to 100 characters or less'
              className='form-input'
              required
            />

            <label>Question Text</label>
            <textarea
              value={questionText}
              onChange={e => setQuestionText(e.target.value)}
              placeholder='Add details'
              className='form-input'
            />

            <label>Tags</label>
            <input
              type='text'
              value={questionTags}
              onChange={e => setQuestionTags(e.target.value)}
              placeholder='Add keywords separated by whitespace'
              className='form-input'
            />
          </div>
        )}

        {!event?._id && (
          <div className='visibility-selection'>
            <label>Visibility</label>
            <select
              value={visibility}
              onChange={e => setVisibility(e.target.value as 'public' | 'private')}>
              <option value='public'>Public</option>
              <option value='private'>Private</option>
            </select>
          </div>
        )}

        <label>
          <input type='checkbox' checked={isMajor} onChange={e => setIsMajor(e.target.checked)} />
          Mark as major event
        </label>

        <label>Start Time</label>
        <input
          type='datetime-local'
          value={startTime}
          onChange={e => setStartTime(e.target.value)}
        />

        <label>End Time</label>
        <input type='datetime-local' value={endTime} onChange={e => setEndTime(e.target.value)} />

        <div className='form-buttons'>
          <button
            type='submit'
            disabled={
              isSubmitting ||
              (selectedTag === 'Watch Party' && !watchPartyTitle.trim()) ||
              (selectedTag === 'Q+A' && !questionTitle.trim())
            }>
            {getSubmitButtonText()}
          </button>

          <button type='button' onClick={onClose} style={{ marginLeft: '10px' }}>
            Cancel
          </button>
        </div>

        {error && <div>{error}</div>}
      </form>
    </div>
  );
};

export default EventForm;
